'use client';

import { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import Head from 'next/head';
import Navbar from "@/components/layout/Navbar";
import MobileNavWrapper from "@/components/layout/MobileNavWrapper";
import Footer from "@/components/layout/Footer";
import BaseSchema from "@/components/seo/BaseSchema";
import FAQSchema, { listingsFAQs } from "@/components/seo/FAQSchema";
import BreadcrumbSchema from "@/components/seo/BreadcrumbSchema";
import ListingsPaywall from "@/components/listings-routes/organisms/ListingsPaywall";
import { 
  ListingsSearchFilter, 
  FeaturedCarousel, 
  PropertyGrid 
} from '@/components/features/listings';
import { 
  rentProperties, 
  saleProperties, 
  sponsoredListings, 
  featuredListings 
} from '@/components/features/listings/mockData';

// Define filter state type
interface FilterState {
  query: string;
  state: string;
  city: string;
  propertyType: string;
  listingType: string;
}

/**
 * ListingsPage - Client Component with preserved functionality
 * 
 * Currently shows paywall to all users (showPaywall = true)
 * Original content is fully preserved and ready to display
 * 
 * To restore listings: change showPaywall to false or add auth logic
 */
export default function ListingsPage() {
  const [filters, setFilters] = useState<FilterState>({
    query: '',
    state: '',
    city: '',
    propertyType: '',
    listingType: '',
  });
  
  // PAYWALL CONTROL - Set to false to show original content
  const showPaywall = true; // TODO: Replace with actual auth/access logic
  
  // Preserve all original filtering logic
  const filteredRentals = useMemo(() => {
    return rentProperties.filter(property => {
      // Search query - check title and description
      if (filters.query &&
          !property.title.toLowerCase().includes(filters.query.toLowerCase()) &&
          !property.description.toLowerCase().includes(filters.query.toLowerCase())) {
        return false;
      }

      // State filter
      if (filters.state && property.address.state.toLowerCase() !== filters.state.toLowerCase()) {
        return false;
      }

      // City filter
      if (filters.city && property.address.city.toLowerCase() !== filters.city.toLowerCase()) {
        return false;
      }

      // Property type filter (placeholder for future implementation)
      // if (filters.propertyType && property.type !== filters.propertyType) {
      //   return false;
      // }

      // Only show rentals if filtering by listing type
      if (filters.listingType === 'for-sale') {
        return false;
      }

      return true;
    });
  }, [filters]);

  const filteredSales = useMemo(() => {
    return saleProperties.filter(property => {
      // Search query - check title and description
      if (filters.query &&
          !property.title.toLowerCase().includes(filters.query.toLowerCase()) &&
          !property.description.toLowerCase().includes(filters.query.toLowerCase())) {
        return false;
      }

      // State filter
      if (filters.state && property.address.state.toLowerCase() !== filters.state.toLowerCase()) {
        return false;
      }

      // City filter
      if (filters.city && property.address.city.toLowerCase() !== filters.city.toLowerCase()) {
        return false;
      }

      // Property type filter (placeholder for future implementation)
      // if (filters.propertyType && property.type !== filters.propertyType) {
      //   return false;
      // }

      // Only show sales if filtering by listing type
      if (filters.listingType === 'for-rent') {
        return false;
      }

      return true;
    });
  }, [filters]);

  const breadcrumbItems = [
    { name: "Home", url: "https://rentwise.co" },
    { name: "Property Listings", url: "https://rentwise.co/listings" }
  ];

  return (
    <>
      <Head>
        <title>Property Listings - Rent & Sale Listings</title>
        <meta name="description" content="Browse verified property listings for rent and sale in Nigeria. Find your perfect home with real tenant reviews and transparent pricing." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://rentwise.co/listings" />
      </Head>
      
      <BaseSchema pageUrl="https://rentwise.co/listings" pageTitle="Property Listings - Rent & Sale" />
      <FAQSchema faqs={listingsFAQs} />
      <BreadcrumbSchema items={breadcrumbItems} />
      
      <MobileNavWrapper>
        <div className="bg-gradient-to-b from-white to-blue-50/50 flex flex-col min-h-screen">
          <Navbar initialAuth={{ isAuthenticated: false, userType: null }} />
          
          <main className="grow">
          {showPaywall ? (
            /* PAYWALL - Currently shown to all users */
            <div className="flex items-center justify-center min-h-[80vh]">
              <ListingsPaywall />
            </div>
          ) : (
            /* ORIGINAL LISTINGS CONTENT - Preserved and ready to display */
            <div className="container mx-auto px-4 py-6 md:py-12 max-w-7xl">
              {/* Hero Section */}
              <motion.div 
                className="mb-10 md:mb-16 text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                  Find Your Perfect Nigerian Property
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Browse our curated selection of premium properties available for rent and sale across Nigeria.
                </p>
              </motion.div>
              
              {/* Search and Filter Bar */}
              <ListingsSearchFilter 
                onFilterChange={setFilters} 
                className="mb-10 md:mb-16"
              />
              
              {/* Sponsored Listings Carousel */}
              <FeaturedCarousel
                title="Sponsored Listings"
                subtitle="Premium properties featured by our trusted partners"
                properties={sponsoredListings}
                type="sponsored"
                className="mb-14 md:mb-20"
              />
              
              {/* Properties for Rent */}
              <PropertyGrid
                title="Properties for Rent"
                properties={filteredRentals}
                type="rent"
                className="mb-16 md:mb-24"
              />
              
              {/* Featured Listings Carousel */}
              <FeaturedCarousel
                title="Featured Listings"
                subtitle="Handpicked premium properties you might love"
                properties={featuredListings}
                type="featured"
                className="mb-14 md:mb-20"
              />
              
              {/* Properties for Sale */}
              <PropertyGrid
                title="Properties for Sale"
                properties={filteredSales}
                type="sale"
              />
            </div>
          )}
        </main>
        
        <Footer />
      </div>
    </MobileNavWrapper>
    </>
  );
} 